<script setup>
import { i18n } from 'boot/i18n';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs.vue';
import { useComponentDialog } from 'src/composables/componentDialog';
import { HrDismissBtn, HrWarningBanner } from 'src/design-system';
import { onMounted, ref, watch } from 'vue';
import draggable from 'vuedraggable';

import { useCareModelCohorts } from '../composables/useCareModelCohorts';
import { useCohortAutoAssignAnalytics } from '../composables/useCohortAutoAssignAnalytics';
import { useCohortSaveWorkflow } from '../composables/useCohortSaveWorkflow';
import { useCohortsAnalytics } from '../composables/useCohortsAnalytics';
import EditCohortDialog from './EditCohortDialog.vue';
import ReviewAssignCohortsDialog from './ReviewAssignCohortsDialog.vue';

defineOptions({ name: 'CohortsSettingsPage' });

const COHORT_AUTO_ASSIGN_SURFACE = 'settings_cohorts';

const { openComponentDialog } = useComponentDialog();

const {
  isLoading,
  isProcessing,
  isRefreshingReview,
  cohorts,
  sortedCohorts,
  assignedClientsCountByCohort,
  clientsNeedingCohortReviewCount,
  clientsNeedingCohortReviewMessage,
  hasCohorts,
  ensureClientsLoaded,
  fetchCohorts,
  refreshReviewState,
  saveOneCohort,
  deleteOneCohort,
  reorderCohorts,
  getFilterLabels,
  setCohorts,
} = useCareModelCohorts();

const { handleEditCohortResult } = useCohortSaveWorkflow({
  ensureClientsLoaded,
  saveOneCohort,
  deleteOneCohort,
  setCohorts,
});

const { trackCohortDialogOpened } = useCohortsAnalytics();
const { trackBannerShown, trackBannerClicked } = useCohortAutoAssignAnalytics(COHORT_AUTO_ASSIGN_SURFACE);

const drag = ref(false);
const bannerShown = ref(false);

const cohortsColumns = [
  {
    name: 'cohortName',
    required: true,
    label: i18n.t('cohorts.fields.cohortName'),
    align: 'left',
    field: 'cohortName',
    sortable: true,
  },
  {
    name: 'assignedClients',
    required: true,
    label: i18n.t('cohorts.fields.assignedClients'),
    align: 'left',
    field: 'assignedClients',
    sortable: true,
  },
  {
    name: 'filters',
    required: true,
    label: i18n.t('cohorts.fields.filters'),
    align: 'left',
    field: 'filters',
    sortable: false,
  },
];

/**
 * Get options for draggable component.
 * @returns {object} - Options for draggable component
 */
function getDragOptions() {
  return {
    animation: 200,
    disabled: false,
    ghostClass: 'ghost',
    direction: 'vertical',
  };
} // getDragOptions()

function onEditCohort(cohort = {}) {
  const cohortEditAttemptId = trackCohortDialogOpened({
    cohort,
    cohorts: sortedCohorts.value,
  });

  openComponentDialog(
    EditCohortDialog,
    {
      cohort,
      cohortEditAttemptId,
      assignedClientsCount: assignedClientsCountByCohort.value[cohort?.id] ?? 0,
    },
    async (result) => {
      await handleEditCohortResult({
        ...result,
        currentCohorts: cohorts.value,
        cohortEditAttemptId,
      });
    },
  );
} // onEditCohort()

function onReviewAndAssign() {
  trackBannerClicked(clientsNeedingCohortReviewCount.value);

  openComponentDialog(
    ReviewAssignCohortsDialog,
    {
      cohorts: cohorts.value,
      surface: COHORT_AUTO_ASSIGN_SURFACE,
    },
    async () => {
      await refreshReviewState();
    },
  );
} // onReviewAndAssign()

watch(
  () => hasCohorts.value && clientsNeedingCohortReviewCount.value > 0,
  (isVisible) => {
    if (isVisible && !bannerShown.value) {
      bannerShown.value = true;
      trackBannerShown(clientsNeedingCohortReviewCount.value);
    }

    if (!isVisible) {
      bannerShown.value = false;
    }
  },
  { immediate: true },
);

onMounted(fetchCohorts);
</script>

<template>
  <q-page
    :class="{
      'q-pa-sm': $q.screen.lt.sm,
      'q-pa-md': !$q.screen.lt.sm,
    }"
  >
    <breadcrumbs :no-link="['settings', 'care-model']" />
    <p class="q-pb-md">
      {{ i18n.t('settings.careModel.cohorts.textCaption') }}
    </p>

    <hr-warning-banner
      v-if="hasCohorts && clientsNeedingCohortReviewCount > 0"
      class="q-mb-md"
      :message="clientsNeedingCohortReviewMessage"
    >
      <template #action>
        <hr-dismiss-btn
          :label="i18n.t('settings.careModel.cohorts.buttonReviewAssign')"
          :loading="isRefreshingReview"
          :disable="isRefreshingReview"
          @click="onReviewAndAssign"
        />
      </template>
    </hr-warning-banner>

    <div class="row q-pb-md col items-top">
      <q-markup-table
        :columns="cohortsColumns"
        :loading="isLoading || isProcessing"
        flat
        bordered
        class="col-12"
      >
        <thead>
          <q-tr>
            <q-th
              colspan="100%"
              class="text-left"
              style="border: none"
            >
              <div class="q-py-sm row items-center justify-between">
                <q-btn
                  color="primary"
                  :disable="isLoading || isProcessing"
                  :label="i18n.t('settings.careModel.cohorts.buttonAddCohort')"
                  @click="onEditCohort()"
                />
              </div>
            </q-th>
          </q-tr>
          <q-tr v-if="hasCohorts">
            <q-th auto-width />
            <q-th class="text-left">
              {{ i18n.t('cohorts.fields.cohortName') }}
            </q-th>
            <q-th class="text-left">
              {{ i18n.t('cohorts.fields.assignedClients') }}
            </q-th>
            <q-th class="text-left">
              {{ i18n.t('cohorts.fields.filters') }}
            </q-th>
          </q-tr>
        </thead>

        <q-tr v-if="!hasCohorts && !isLoading">
          <q-td colspan="100%">
            {{ i18n.t('settings.careModel.cohorts.tableNoDataLabel') }}
          </q-td>
        </q-tr>

        <draggable
          v-bind="getDragOptions()"
          :model-value="sortedCohorts"
          class="list-group q-mb-lg"
          tag="tbody"
          handle=".target-drag-handle"
          :force-fallback="true"
          item-key="id"
          @update:model-value="reorderCohorts"
          @start="drag = true"
          @end="drag = false"
        >
          <template #item="{ element: cohort }">
            <q-tr class="hover-detect">
              <q-td class="nostretch">
                <q-icon
                  flat
                  size="sm"
                  color="dark-variant"
                  name="drag_indicator"
                  class="target-drag-handle show-on-hover"
                />
              </q-td>
              <q-td
                class="cursor-pointer"
                @click="onEditCohort(cohort)"
              >
                {{ cohort.cohortName }}
              </q-td>
              <q-td
                class="cursor-pointer"
                @click="onEditCohort(cohort)"
              >
                {{ assignedClientsCountByCohort[cohort.id] || 0 }}
              </q-td>
              <q-td
                class="cursor-pointer"
                @click="onEditCohort(cohort)"
              >
                <div class="row q-gutter-xs">
                  <q-chip
                    v-for="(label, idx) in getFilterLabels(cohort.filters)"
                    :key="idx"
                    size="sm"
                    color="grey-3"
                    text-color="black"
                    class="table-filter-chip"
                  >
                    {{ label }}
                  </q-chip>
                </div>
              </q-td>
            </q-tr>
          </template>
        </draggable>

        <q-inner-loading :showing="isLoading || isProcessing" />
      </q-markup-table>
    </div>
  </q-page>
</template>

<style lang="scss" scoped>
.hover-detect .show-on-hover {
  visibility: hidden;
}

@media (hover: none), (pointer: coarse) {
  .hover-detect .show-on-hover {
    visibility: visible;
  }
}

.hover-detect:hover .show-on-hover {
  visibility: visible;
}

.target-drag-handle {
  cursor: move;
}

.table-filter-chip {
  max-width: 260px;
}
</style>
